import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { track } from "../analytics";
import "./Converter.css";

const API_BASE = process.env.REACT_APP_API_BASE; // "" in dev (proxied), absolute in prod
const SAMPLE_EPS = "/demo/test.eps";

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Converter() {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const [jobId, setJobId] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(""); // "png" | "jpg" | "pdf"
  const [zoom, setZoom] = useState(false);

  const baseName = () => (fileName || "eps").replace(/\.[^/.]+$/, "");

  // Close the lightbox with Escape.
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  const reset = () => {
    setPreviewUrl(null);
    setError("");
    setProgress(0);
    setFileName("");
    setJobId("");
    setPdfUrl("");
    setZoom(false);
  };

  const process = async (file, source = "browse") => {
    setError("");
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".eps")) {
      setError("Unsupported file. Please choose a .eps file.");
      return;
    }

    setFileName(file.name);
    setPreviewUrl(null);
    setLoading(true);
    setProgress(8);
    track("fileUploadStart", { source });

    const fail = (code) => {
      const e = new Error(code);
      e.code = code;
      return e;
    };

    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await axios.post(`${API_BASE}/upload`, formData, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.min(45, Math.round((e.loaded * 45) / e.total)));
        },
      });
      if (!uploadRes.data.folderName) throw fail("upload");
      const id = uploadRes.data.jobId;
      setJobId(id);
      setProgress(55);

      // Poll until the backend reports success — but stop immediately if it
      // reports a definitive failure (don't wait out the timeout).
      let status = "";
      let attempts = 0;
      while (attempts < 50) {
        const rec = await axios.post(`${API_BASE}/records`, { jobId: id });
        status = rec.data.status;
        if (status === "success") break;
        if (status === "failed" || status === "error") throw fail("convert");
        attempts += 1;
        setProgress((p) => Math.min(85, p + 2));
        await new Promise((r) => setTimeout(r, 600));
      }
      if (status !== "success") throw fail("timeout");

      setProgress(88);
      const dl = await axios.post(`${API_BASE}/download`, { jobId: id });
      if (dl.data?.url) setPdfUrl(dl.data.url);

      setProgress(92);
      const previewRes = await axios.post(
        `${API_BASE}/preview`,
        { jobId: id },
        { responseType: "arraybuffer" }
      );
      const blob = new Blob([previewRes.data], { type: "image/png" });
      setPreviewUrl(URL.createObjectURL(blob));
      setProgress(100);
      track("filePreviewSuccess", { source });
    } catch (err) {
      // Map every failure to a clean, user-friendly message. The backend's raw
      // errorMessage (a .NET stack trace) is never shown to the user.
      const code = err?.code || (err?.message === "Network Error" ? "network" : "other");
      const MESSAGES = {
        upload: "Upload failed. Please try again.",
        convert: "We couldn't process this file. Please make sure it's a valid, uncorrupted .eps file.",
        timeout: "This is taking longer than expected. Please try again in a moment.",
        network: "Could not reach the converter. Please check your connection and try again.",
        other: "Something went wrong while converting your file. Please try again.",
      };
      track("filePrevFail", { source, reason: code });
      setError(MESSAGES[code] || MESSAGES.other);
    } finally {
      setLoading(false);
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) process(file, "browse");
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) process(file, "drop");
  };
  const onDrag = (e) => {
    e.preventDefault();
    setDragging(e.type === "dragenter" || e.type === "dragover");
  };

  const loadSample = async () => {
    try {
      setError("");
      track("btnPress_trySample");
      const res = await fetch(SAMPLE_EPS);
      const blob = await res.blob();
      process(new File([blob], "sample.eps", { type: "application/postscript" }), "sample");
    } catch {
      track("filePrevFail", { source: "sample", message: "sample fetch failed" });
      setError("Could not load the sample file.");
    }
  };

  /* ---- Downloads ---- */
  const savePng = async () => {
    if (!jobId) return;
    try {
      setSaving("png");
      track("download_png");
      const res = await axios.post(`${API_BASE}/download-png`, { jobId }, { responseType: "blob" });
      triggerDownload(new Blob([res.data], { type: "image/png" }), baseName() + ".png");
    } catch {
      setError("PNG download failed. Please try again.");
    } finally {
      setSaving("");
    }
  };

  const saveJpg = async () => {
    if (!previewUrl) return;
    try {
      setSaving("jpg");
      track("download_jpg");
      const img = new Image();
      const blob = await new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff"; // JPG has no transparency
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode failed"))), "image/jpeg", 0.92);
        };
        img.onerror = () => reject(new Error("image load failed"));
        img.src = previewUrl;
      });
      triggerDownload(blob, baseName() + ".jpg");
    } catch {
      setError("JPG download failed. Please try again.");
    } finally {
      setSaving("");
    }
  };

  const savePdf = async () => {
    if (!pdfUrl) return;
    try {
      setSaving("pdf");
      track("download_pdf");
      const fetchUrl = API_BASE ? pdfUrl : new URL(pdfUrl).pathname;
      const res = await axios.get(fetchUrl, { responseType: "blob" });
      triggerDownload(new Blob([res.data], { type: "application/pdf" }), baseName() + ".pdf");
    } catch {
      if (pdfUrl) window.open(pdfUrl, "_blank", "noopener");
      else setError("PDF download failed. Please try again.");
    } finally {
      setSaving("");
    }
  };

  const DownloadButtons = () => (
    <div className="dl-row">
      <button className="dl-btn" onClick={savePng} disabled={!!saving}>
        <span className="dl-btn__ico" aria-hidden="true">⬇</span>
        {saving === "png" ? "Preparing…" : "Download PNG"}
      </button>
      <button className="dl-btn" onClick={saveJpg} disabled={!!saving}>
        <span className="dl-btn__ico" aria-hidden="true">⬇</span>
        {saving === "jpg" ? "Preparing…" : "Download JPG"}
      </button>
      <button className="dl-btn" onClick={savePdf} disabled={!!saving || !pdfUrl}>
        <span className="dl-btn__ico" aria-hidden="true">⬇</span>
        {saving === "pdf" ? "Preparing…" : "Download PDF"}
      </button>
    </div>
  );

  return (
    <div className="converter card">
      {!previewUrl && !loading && (
        <div
          className={`dropzone ${dragging ? "is-drag" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragEnter={onDrag}
          onDragOver={onDrag}
          onDragLeave={onDrag}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
        >
          <div className="dropzone__icon" aria-hidden="true">⬆</div>
          <p className="dropzone__title">Drag &amp; drop your EPS file</p>
          <p className="dropzone__or">or</p>
          <div className="dropzone__actions">
            <button type="button" className="btn btn--primary" onClick={() => track("btnPress_browseFiles")}>Browse files</button>
            <button type="button" className="btn btn--ghost" onClick={(e) => { e.stopPropagation(); loadSample(); }}>
              Try a sample
            </button>
          </div>
          <p className="dropzone__hint">Convert to PNG, JPG or PDF · processed securely &amp; removed after preview.</p>
          <input ref={inputRef} type="file" accept=".eps,application/postscript" hidden onChange={onInputChange} />
        </div>
      )}

      {loading && (
        <div className="converter__loading">
          <div className="spinner" aria-hidden="true" />
          <p className="converter__status">Processing {fileName}…</p>
          <div className="progress"><div className="progress__bar" style={{ width: `${progress}%` }} /></div>
          <p className="converter__pct">{progress}%</p>
        </div>
      )}

      {previewUrl && !loading && (
        <div className="converter__result">
          <div
            className="converter__preview"
            onClick={() => setZoom(true)}
            role="button"
            tabIndex={0}
            title="Click to enlarge"
          >
            <img src={previewUrl} alt={`Preview of ${fileName}`} />
            <span className="converter__zoomhint" aria-hidden="true">🔍 Click to enlarge</span>
          </div>

          <div className="converter__filemeta">
            <span className="converter__check" aria-hidden="true">✓</span>
            <span title={fileName}>{fileName}</span>
          </div>

          <p className="converter__savelabel">Choose a format to download</p>
          <DownloadButtons />

          <button className="btn btn--ghost converter__again" onClick={reset} disabled={!!saving}>
            ↺ Convert another file
          </button>
        </div>
      )}

      {error && <p className="converter__error" role="alert">{error}</p>}

      {zoom && previewUrl && (
        <div className="lightbox" onClick={() => setZoom(false)} role="dialog" aria-modal="true" aria-label="Enlarged preview">
          <button className="lightbox__close" onClick={() => setZoom(false)} aria-label="Close preview">×</button>
          <div className="lightbox__inner" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox__imgwrap">
              <img src={previewUrl} alt={`Enlarged preview of ${fileName}`} />
            </div>
            <div className="lightbox__bar">
              <span className="lightbox__name" title={fileName}>{fileName}</span>
              <DownloadButtons />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
